import { useState } from 'react';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { commitRateLineImport, previewRateLineImport, type RateLineImportCommitResult, type RateLineImportPreviewResult } from '../../../rate-cards.api';

export type RateLineImportState =
  | { status: 'idle' }
  | { status: 'previewing' }
  | { status: 'preview-error'; message: string }
  | { status: 'previewed'; file: File; result: RateLineImportPreviewResult }
  | { status: 'committing'; file: File; result: RateLineImportPreviewResult }
  | { status: 'commit-error'; file: File; result: RateLineImportPreviewResult; message: string }
  | { status: 'committed'; result: RateLineImportCommitResult };

const PREVIEW_FAILURE = "Could not read this file — check it's a .xlsx exported from this card or the template.";
const COMMIT_FAILURE = 'Could not commit the import.';

/** Preview and commit each post the same file the browser already holds (gitaumoses4/chukua#28): nothing is cached on the server between the two calls. */
export function useRateLineImport(rateCardId: string) {
  const [state, setState] = useState<RateLineImportState>({ status: 'idle' });

  async function preview(file: File): Promise<void> {
    setState({ status: 'previewing' });
    try {
      const result = await previewRateLineImport(rateCardId, file);
      setState({ status: 'previewed', file, result });
    } catch (err) {
      setState({ status: 'preview-error', message: apiErrorMessage(err) ?? PREVIEW_FAILURE });
    }
  }

  /** Resolves with the commit result on success, so the caller can toast without racing a render effect on `state`. */
  async function commit(reason: string): Promise<RateLineImportCommitResult | undefined> {
    if (state.status !== 'previewed' && state.status !== 'commit-error') return undefined;
    const { file, result } = state;
    setState({ status: 'committing', file, result });
    try {
      const commitResult = await commitRateLineImport(rateCardId, file, reason);
      setState({ status: 'committed', result: commitResult });
      return commitResult;
    } catch (err) {
      setState({ status: 'commit-error', file, result, message: apiErrorMessage(err) ?? COMMIT_FAILURE });
      return undefined;
    }
  }

  function reset(): void {
    setState({ status: 'idle' });
  }

  return { state, preview, commit, reset };
}
